import { useEffect, useState } from 'react';
import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/client/components/ui/dialog';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { Textarea } from '@/client/components/ui/textarea';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { CategoryCombobox } from './CategoryCombobox';

type FinanceRecord = RouterOutputs['finance']['listRecords'][number];

type RecordValues = {
  date: string;
  amount: string;
  type: 'INCOME' | 'OUTCOME';
  category: string;
  description: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editRecord?: FinanceRecord;
  onSuccess: () => void;
};

function emptyValues(): RecordValues {
  return {
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    type: 'INCOME',
    category: '',
    description: '',
  };
}

function recordToValues(r: FinanceRecord): RecordValues {
  return {
    date: r.date,
    amount: String(r.amount),
    type: r.type,
    category: r.category,
    description: r.description,
  };
}

export function FinanceRecordDialog({ open, onOpenChange, editRecord, onSuccess }: Props) {
  const isEdit = !!editRecord;
  const [values, setValues] = useState<RecordValues>(emptyValues);

  useEffect(() => {
    if (open) {
      setValues(editRecord ? recordToValues(editRecord) : emptyValues());
    }
  }, [open, editRecord]);

  const utils = trpc.useUtils();
  const { data: categories = [] } = trpc.finance.listCategories.useQuery();
  const { data: latestRecords = [] } = trpc.finance.latestRecords.useQuery(undefined, {
    enabled: !isEdit,
  });

  const createMutation = trpc.finance.createRecord.useMutation({
    onSuccess: () => {
      utils.finance.listRecords.invalidate();
      utils.finance.listCategories.invalidate();
      utils.finance.latestRecords.invalidate();
      onSuccess();
      onOpenChange(false);
    },
  });

  const updateMutation = trpc.finance.updateRecord.useMutation({
    onSuccess: () => {
      utils.finance.listRecords.invalidate();
      utils.finance.listCategories.invalidate();
      utils.finance.latestRecords.invalidate();
      onSuccess();
      onOpenChange(false);
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error?.message ?? updateMutation.error?.message ?? null;

  function set<K extends keyof RecordValues>(key: K, val: RecordValues[K]) {
    setValues((prev) => ({ ...prev, [key]: val }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const amount = Number.parseFloat(values.amount);
    if (Number.isNaN(amount) || amount <= 0) return;

    if (isEdit && editRecord) {
      updateMutation.mutate({
        id: editRecord.id,
        date: values.date,
        amount,
        type: values.type,
        category: values.category,
        description: values.description,
      });
    } else {
      createMutation.mutate({
        date: values.date,
        amount,
        type: values.type,
        category: values.category,
        description: values.description,
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit record' : 'Add record'}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-6">
          <form id="finance-record-form" onSubmit={handleSubmit} className="flex-1 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fin-date">Date</Label>
              <Input
                id="fin-date"
                type="date"
                value={values.date}
                onChange={(e) => set('date', e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fin-amount">Amount (€)</Label>
              <Input
                id="fin-amount"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={values.amount}
                onChange={(e) => set('amount', e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fin-type">Type</Label>
              <Select
                value={values.type}
                onValueChange={(v) => set('type', v as 'INCOME' | 'OUTCOME')}
              >
                <SelectTrigger id="fin-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INCOME">Income</SelectItem>
                  <SelectItem value="OUTCOME">Outcome</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <CategoryCombobox
                value={values.category}
                onChange={(v) => set('category', v)}
                categories={categories}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fin-description">Description</Label>
              <Textarea
                id="fin-description"
                placeholder="Description…"
                value={values.description}
                onChange={(e) => set('description', e.target.value)}
                required
              />
            </div>
          </form>

          {!isEdit && latestRecords.length > 0 && (
            <div className="w-52 shrink-0">
              <p className="mb-2 text-sm font-medium text-muted-foreground">Recent records</p>
              <div className="max-h-80 space-y-1 overflow-y-auto">
                {latestRecords.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() =>
                      setValues((prev) => ({
                        date: prev.date,
                        amount: String(r.amount),
                        type: r.type,
                        category: r.category,
                        description: r.description,
                      }))
                    }
                    className="w-full rounded-md border px-3 py-2 text-left text-xs transition-colors hover:bg-accent"
                  >
                    <div className="mb-0.5 flex items-center justify-between gap-1">
                      <Badge
                        variant="outline"
                        className={
                          r.type === 'INCOME'
                            ? 'border-green-200 text-green-700'
                            : 'border-red-200 text-red-700'
                        }
                      >
                        {r.type === 'INCOME' ? 'In' : 'Out'}
                      </Badge>
                      <span className="font-medium">€{r.amount.toFixed(2)}</span>
                    </div>
                    <div className="truncate text-muted-foreground">{r.category}</div>
                    <div className="truncate">{r.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {mutationError && <p className="text-sm text-destructive">{mutationError}</p>}

        <DialogFooter>
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="finance-record-form" disabled={isPending}>
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
